/**
 * Admin-UI: gemeldete Plusstunden genehmigen / ablehnen / korrigieren / manuell erfassen.
 * Wird unter Honorare & Gehälter als eigener Tab genutzt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, History, Loader2, Pencil, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime, formatDeNumber } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import { filterPlusHours, sortPlusHours } from './plusHoursList';

const STATUS = {
  gemeldet: 'bg-amber-100 text-amber-700',
  genehmigt: 'bg-emerald-100 text-emerald-700',
  abgelehnt: 'bg-rose-100 text-rose-700',
  abgerechnet: 'bg-slate-200 text-slate-700',
};

const ACTION_LABEL = {
  create: 'Erfasst',
  approve: 'Genehmigt',
  reject: 'Abgelehnt',
  correct: 'Korrektur',
};

function fmtH(h) {
  return formatDeNumber(h);
}

function DecisionHistoryList({ entries }) {
  if (!entries?.length) {
    return <p className="text-xs text-slate-400">Noch keine Historie.</p>;
  }
  return (
    <ul className="space-y-2 max-h-48 overflow-y-auto" data-testid="plus-hours-history-list">
      {[...entries].reverse().map((e, i) => (
        <li key={`${e.at}-${e.action}-${i}`} className="text-xs border-b border-slate-100 pb-2 last:border-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-medium text-slate-800">{ACTION_LABEL[e.action] || e.action}</span>
            {e.action === 'correct' && e.hours_before != null && e.hours_after != null && (
              <span className="tabular-nums text-slate-600">
                {fmtH(e.hours_before)} → {fmtH(e.hours_after)} Std.
              </span>
            )}
            <span className="text-slate-400 ml-auto">{e.at ? formatDateTime(e.at) : '—'}</span>
          </div>
          <div className="text-slate-500 mt-0.5">
            {e.user_name || '—'}
            {e.notes ? ` · ${e.notes}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function PlusHoursAdminTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('gemeldet');
  const [teacherId, setTeacherId] = useState('');
  const [exactDate, setExactDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sort, setSort] = useState({ key: 'work_date', dir: 'desc' });
  const [decision, setDecision] = useState(null);
  const [correct, setCorrect] = useState(null); // { row, hours, notes }
  const [historyRow, setHistoryRow] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    teacher_id: '', work_date: new Date().toISOString().slice(0, 10),
    hours: '', activity: '', notes: '', auto_approve: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get('/admin/plus-hours', { params: { limit: 1000 } }),
        api.get('/teachers').catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setTeachers(Array.isArray(t.data) ? t.data : (t.data?.items || []));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!decision) return;
    setSaving(true);
    try {
      await api.post(`/admin/plus-hours/${decision.row.id}/${decision.action}`, {
        notes: decision.notes || null,
      });
      toast.success(decision.action === 'approve' ? 'Plusstunden genehmigt' : 'Plusstunden abgelehnt');
      setDecision(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const submitCorrect = async () => {
    if (!correct) return;
    const hours = parseFloat(String(correct.hours).replace(',', '.'));
    if (!(hours > 0)) {
      toast.error('Bitte gültige Stunden eingeben');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/plus-hours/${correct.row.id}/correct`, {
        hours,
        notes: (correct.notes || '').trim() || null,
      });
      toast.success('Korrektur gespeichert');
      setCorrect(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Korrektur fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const submitCreate = async () => {
    if (!createForm.teacher_id || !createForm.work_date || !createForm.hours || !(createForm.activity || '').trim()) {
      toast.error('Lehrkraft, Datum, Stunden und Tätigkeit sind erforderlich');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/plus-hours', {
        teacher_id: createForm.teacher_id,
        work_date: createForm.work_date,
        hours: parseFloat(String(createForm.hours).replace(',', '.')) || 0,
        activity: createForm.activity.trim(),
        notes: (createForm.notes || '').trim() || null,
        auto_approve: !!createForm.auto_approve,
      });
      toast.success(createForm.auto_approve ? 'Plusstunden erfasst und genehmigt' : 'Plusstunden erfasst');
      setCreateOpen(false);
      setCreateForm({
        teacher_id: '', work_date: new Date().toISOString().slice(0, 10),
        hours: '', activity: '', notes: '', auto_approve: true,
      });
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erfassen fehlgeschlagen');
    } finally { setCreating(false); }
  };

  const canCorrect = (r) => r.status !== 'abgerechnet';
  const visible = sortPlusHours(filterPlusHours(rows, {
    status: filter,
    teacherId,
    date: exactDate,
    from: fromDate,
    to: toDate,
  }), sort.key, sort.dir);
  const toggleSort = (key) => {
    setSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };
  const sortLabel = (key, label) => (
    <button
      type="button"
      className="uppercase tracking-wide hover:text-slate-800"
      onClick={() => toggleSort(key)}
      data-testid={`plus-hours-sort-${key}`}
    >
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="plus-hours-admin-tab">
      <div className="flex gap-2 items-end justify-between flex-wrap">
        <div className="flex gap-2 items-end flex-wrap">
          <label className="text-xs text-slate-500">
            Status
            <select
              className="mt-1 block h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="plus-hours-filter"
            >
              <option value="gemeldet">Offen (gemeldet)</option>
              <option value="genehmigt">Genehmigt</option>
              <option value="abgelehnt">Abgelehnt</option>
              <option value="abgerechnet">Abgerechnet</option>
              <option value="all">Alle</option>
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Lehrkraft
            <select
              className="mt-1 block h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
              data-testid="plus-hours-teacher"
            >
              <option value="">alle</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>{t.last_name}, {t.first_name}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Datum
            <DateInput
              value={exactDate}
              onChange={(e) => setExactDate(e.target.value)}
              className="mt-1 h-8 w-36"
              data-testid="plus-hours-date"
            />
          </label>
          <label className="text-xs text-slate-500">
            Von
            <DateInput
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 h-8 w-36"
              data-testid="plus-hours-from"
            />
          </label>
          <label className="text-xs text-slate-500">
            Bis
            <DateInput
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 h-8 w-36"
              data-testid="plus-hours-to"
            />
          </label>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="plus-hours-create">
          <Clock className="w-3.5 h-3.5 mr-1" /> Manuell erfassen
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-6 text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin" /> Laden…</div>
        ) : visible.length === 0 ? (
          <div className="p-6 text-slate-400">Keine Plusstunden-Meldungen</div>
        ) : (
          <table className="w-full text-sm" data-testid="plus-hours-admin-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">{sortLabel('work_date', 'Datum')}</th>
                <th className="text-left px-4 py-3">{sortLabel('teacher', 'Lehrkraft')}</th>
                <th className="text-right px-4 py-3">{sortLabel('hours', 'Stunden')}</th>
                <th className="text-left px-4 py-3">{sortLabel('activity', 'Tätigkeit')}</th>
                <th className="text-left px-4 py-3">{sortLabel('status', 'Status')}</th>
                <th className="text-right px-4 py-3">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`plus-hours-row-${r.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.work_date)}</td>
                  <td className="px-4 py-3 font-medium">{r.teacher_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">{fmtH(r.hours)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{r.activity}</div>
                    {r.notes && <div className="text-xs text-slate-400 mt-0.5">{r.notes}</div>}
                    {r.review_notes && <div className="text-xs text-slate-500 mt-0.5 italic">{r.review_notes}</div>}
                  </td>
                  <td className="px-4 py-3"><Badge className={STATUS[r.status] || ''}>{statusLabel(r.status)}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {(r.status === 'gemeldet' || r.status === 'abgelehnt') && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white mr-1"
                        onClick={() => setDecision({ row: r, action: 'approve', notes: '' })}
                        data-testid={`plus-hours-approve-${r.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Genehmigen
                      </Button>
                    )}
                    {(r.status === 'gemeldet' || r.status === 'genehmigt') && (
                      <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 mr-1"
                        onClick={() => setDecision({ row: r, action: 'reject', notes: '' })}
                        data-testid={`plus-hours-reject-${r.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Ablehnen
                      </Button>
                    )}
                    {canCorrect(r) && (
                      <Button size="sm" variant="outline" className="mr-1"
                        onClick={() => setCorrect({
                          row: r,
                          hours: String(r.hours).replace('.', ','),
                          notes: '',
                        })}
                        data-testid={`plus-hours-correct-${r.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Korrektur
                      </Button>
                    )}
                    {(r.decision_history?.length > 0) && (
                      <Button size="sm" variant="ghost" className="text-slate-500"
                        onClick={() => setHistoryRow(r)}
                        title="Historie"
                        data-testid={`plus-hours-history-${r.id}`}>
                        <History className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <Dialog open onOpenChange={(o) => !o && setCreateOpen(false)}>
          <DialogContent data-testid="plus-hours-create-dialog">
            <DialogHeader>
              <DialogTitle>Plusstunden manuell erfassen</DialogTitle>
              <DialogDescription>
                Einmalige Stunden für die Lohnabrechnung – unabhängig von einer Lehrkraft-Meldung.
                Standardmäßig direkt genehmigt (ohne Ferienfaktor, reine Monatsstunden).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Lehrkraft *</label>
                <Select value={createForm.teacher_id || ''} onValueChange={(v) => setCreateForm({ ...createForm, teacher_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="plus-hours-create-teacher"><SelectValue placeholder="Lehrkraft wählen…" /></SelectTrigger>
                  <SelectContent>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Datum *</label>
                  <DateInput
                    value={createForm.work_date}
                    onChange={(e) => setCreateForm({ ...createForm, work_date: e.target.value })}
                    className="mt-1"
                    data-testid="plus-hours-create-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Stunden *</label>
                  <Input
                    value={createForm.hours}
                    onChange={(e) => setCreateForm({ ...createForm, hours: e.target.value })}
                    className="mt-1"
                    placeholder="z. B. 2,5"
                    data-testid="plus-hours-create-hours"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Tätigkeit *</label>
                <Input
                  value={createForm.activity}
                  onChange={(e) => setCreateForm({ ...createForm, activity: e.target.value })}
                  className="mt-1"
                  placeholder="z. B. Konzertbegleitung"
                  data-testid="plus-hours-create-activity"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Notiz (optional)</label>
                <Textarea
                  rows={2}
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={!!createForm.auto_approve}
                  onChange={(e) => setCreateForm({ ...createForm, auto_approve: e.target.checked })}
                  data-testid="plus-hours-create-auto-approve"
                />
                Sofort genehmigen (für Lohnlauf)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
              <Button onClick={submitCreate} disabled={creating} data-testid="plus-hours-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erfassen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {decision && (
        <Dialog open onOpenChange={(o) => !o && setDecision(null)}>
          <DialogContent data-testid="plus-hours-decision-dialog">
            <DialogHeader>
              <DialogTitle>
                {decision.action === 'approve' ? 'Plusstunden genehmigen' : 'Plusstunden ablehnen'}
              </DialogTitle>
              <DialogDescription>
                {decision.row.teacher_name} · {formatDate(decision.row.work_date)} · {fmtH(decision.row.hours)} Std. · {decision.row.activity}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={3}
              placeholder="Notiz an die Lehrkraft (optional)"
              value={decision.notes}
              onChange={(e) => setDecision({ ...decision, notes: e.target.value })}
            />
            {decision.row.decision_history?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                  <History className="w-3.5 h-3.5" /> Historie
                </p>
                <DecisionHistoryList entries={decision.row.decision_history} />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDecision(null)}>Abbrechen</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className={decision.action === 'approve' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (decision.action === 'approve' ? 'Genehmigen' : 'Ablehnen')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {correct && (
        <Dialog open onOpenChange={(o) => !o && setCorrect(null)}>
          <DialogContent data-testid="plus-hours-correct-dialog">
            <DialogHeader>
              <DialogTitle>Stunden korrigieren</DialogTitle>
              <DialogDescription>
                {correct.row.teacher_name} · {formatDate(correct.row.work_date)} · {correct.row.activity}
                {' · aktuell '}{fmtH(correct.row.hours)} Std.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Korrigierte Stunden *</label>
                <Input
                  value={correct.hours}
                  onChange={(e) => setCorrect({ ...correct, hours: e.target.value })}
                  className="mt-1"
                  placeholder="z. B. 3,5"
                  data-testid="plus-hours-correct-hours"
                />
                <p className="text-xs text-slate-400 mt-1">Mehr oder weniger als gemeldet — Status bleibt unverändert.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Begründung (optional)</label>
                <Textarea
                  rows={2}
                  value={correct.notes}
                  onChange={(e) => setCorrect({ ...correct, notes: e.target.value })}
                  className="mt-1"
                  placeholder="z. B. nur 2 Std. nachgewiesen"
                  data-testid="plus-hours-correct-notes"
                />
              </div>
              {correct.row.decision_history?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                    <History className="w-3.5 h-3.5" /> Historie
                  </p>
                  <DecisionHistoryList entries={correct.row.decision_history} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCorrect(null)}>Abbrechen</Button>
              <Button onClick={submitCorrect} disabled={saving} data-testid="plus-hours-correct-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Korrektur speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {historyRow && (
        <Dialog open onOpenChange={(o) => !o && setHistoryRow(null)}>
          <DialogContent data-testid="plus-hours-history-dialog">
            <DialogHeader>
              <DialogTitle>Historie</DialogTitle>
              <DialogDescription>
                {historyRow.teacher_name} · {formatDate(historyRow.work_date)} · {fmtH(historyRow.hours)} Std. · {historyRow.activity}
              </DialogDescription>
            </DialogHeader>
            <DecisionHistoryList entries={historyRow.decision_history} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryRow(null)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
